import { useEffect, useRef } from "react";

type InvocaWindow = Window & {
  Invoca?: {
    PNAPI?: {
      run: () => void;
    };
  };
};

const isPhoneNumberVisible = (phoneNumber: string): boolean => {
  if (typeof document === "undefined") {
    return false;
  }

  const trackedElements = document.querySelectorAll<HTMLElement>(
    '[data-obc-invoca-phone-number="true"]',
  );

  if (trackedElements.length > 0) {
    return Array.from(trackedElements).some((el) => (el.textContent ?? "").includes(phoneNumber));
  }

  return document.body?.textContent?.includes(phoneNumber) ?? false;
};

export const useInvocaPhoneNumberTracking = (phoneNumber?: string) => {
  const hasTriggeredForCurrentVisibilityRef = useRef(false);

  useEffect(() => {
    if (!phoneNumber || typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const attemptTracking = (): boolean => {
      const visible = isPhoneNumberVisible(phoneNumber);

      if (!visible) {
        hasTriggeredForCurrentVisibilityRef.current = false;
        return false;
      }

      if (hasTriggeredForCurrentVisibilityRef.current) {
        return false;
      }

      const invocaWindow = window as InvocaWindow;
      if (typeof invocaWindow.Invoca?.PNAPI?.run !== "function") {
        return false;
      }

      invocaWindow.Invoca.PNAPI.run();
      hasTriggeredForCurrentVisibilityRef.current = true;
      return true;
    };

    attemptTracking();

    const observer = new MutationObserver(() => {
      attemptTracking();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const maxPolls = 120; // ~30s at 250ms
    let pollCount = 0;
    const pollingInterval = window.setInterval(() => {
      pollCount += 1;
      if (attemptTracking() || pollCount >= maxPolls) {
        window.clearInterval(pollingInterval);
      }
    }, 250);

    return () => {
      observer.disconnect();
      window.clearInterval(pollingInterval);
    };
  }, [phoneNumber]);
};
